import {
  BellOutlined,
  CheckSquareOutlined,
  MessageOutlined,
  PlusCircleOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useVisibilityChange } from "@uidotdev/usehooks";
import { Avatar, Badge, Dropdown, Menu, Spin, Tooltip, type MenuProps } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { chatActions, notificationsActions, teamActions, userSelectors } from "@fitness-app/app-store";
import { PermissionKey, RequestStatus } from "@fitness-app/data-models";
import { getUserInitials } from "@fitness-app/utils";

import { useIsMobile, useIsPhone } from "~/hooks/useIsMobile";
import { usePermission } from "~/hooks/usePermission";
import { needsTeamRoster, rosterLimitedToAssigned } from "~/modules/Team/teamRosterAccess";
import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import ChatDrawer, { CHAT_DRAWER_WIDTH } from "~/modules/Chat/ChatDrawer";
import ChatWindowsList from "~/modules/Chat/ChatWindowsList";
import { ChatUtilitiesProvider } from "~/modules/Chat/providers/ChatUtilitiesProvider";
import DashboardWrapper from "~/modules/Dashboard/DashboardWrapper";
import { TasksDrawer, useTeamMemberTasksCount } from "~/modules/Dashboard/TeamMemberTasks";
import { CHALLENGES_FEATURE_FLAG } from "~/modules/Challenges/challengesFeatureFlag";
import { SALES_CRM_FEATURE_FLAG } from "~/modules/Sales/salesFeatureFlag";
import { ADS_STUDIO_FEATURE_FLAG } from "~/modules/Studio/adsStudioFeatureFlag";
import { useSupportUnreadNotifications } from "~/modules/Support/hooks/useSupportUnreadNotifications";
import { SUPPORT_BOARD_FEATURE_FLAG } from "~/modules/Support/supportFeatureFlag";
import { BatchAddWorkoutModal } from "~/modules/Trainee/BatchAddWorkout/BatchAddWorkoutModal";
import { TrainingProgramAgentHost } from "~/modules/TrainingPrograms/TrainingProgramAgent/TrainingProgramAgentHost";
import {
  TRAINER_ASSISTANT_BUBBLE_GUTTER,
  TrainerAssistantHost,
  collapseTrainerAssistantWindow,
  TRAINER_ASSISTANT_WINDOW_GUTTER,
  trainerAssistantWindowFits,
  useTrainerAssistantGutter,
} from "~/modules/TrainerAssistant/TrainerAssistantHost";
import { useTrainerAssistantEnabled } from "~/modules/TrainerAssistant/useTrainerAssistantEnabled";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import BackgroundTasksDrawer from "./BackgroundTasksDrawer/BackgroundTasksDrawer";
import NotificationsDrawer from "./NotificationsDrawer/NotificationsDrawer";
import ProfileDrawer from "./ProfileDrawer/ProfileDrawer";
import { matchesPermissionRequirement } from "../Auth/permissionRequirement";
import { dashboardNavigation } from "./dashboardNavigation";
import { dashboardPermissionRequirements, getDashboardPermissionRequirement } from "./dashboardPermissionRequirements";

export const Dashboard = (): React.ReactElement => {
  const [showChatDrawer, toggleChatDrawer] = useState(false);
  const [showNotificationsDrawer, toggleNotificationDrawer] = useState(false);
  const [showProfileDrawer, toggleProfileDrawer] = useState(false);
  const [showTasksDrawer, toggleTasksDrawer] = useState(false);
  const [showBatchAddWorkoutModal, toggleBatchAddWorkoutModal] = useState(false);
  const { t } = useTranslation(["dashboard", "auth"]);
  const user = useAppSelector((state) => state.user);
  const permissions = useAppSelector(userSelectors.getUserPermissions);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const totalUnread = useAppSelector((store) => store.chat.unreadMessages?.total);
  const channelOpenCount = useAppSelector((store) => store.chat.channelOpenCount);
  const activeChannelsCount = useAppSelector((store) => store.chat.activeChannels.length);
  const unreadNotifications = useAppSelector((state) => state.notifications.totalUnread);
  const unreadMessagesStatus = useAppSelector((state) => state.chat.unreadMessagesStatus);
  const { counts: taskCounts } = useTeamMemberTasksCount();
  const navigate = useNavigate();
  const documentVisible = useVisibilityChange();
  const { isTeamMember } = useUserRole();
  // TRE-2076/TRE-2095: pływające okienka czatu znikają dopiero na telefonie (< 768 px) —
  // na tablecie i w zwężonym oknie desktopu trenerki dalej pracują na okienkach.
  const isPhone = useIsPhone();
  const isMobile = useIsMobile();
  // Roster zasila filtry „Trener" i selektory autora, a nie zarządzanie
  // zespołem — patrz teamRosterAccess. Samo `team.read` ma w katalogu wyłącznie
  // ACCOUNT_OWNER, więc bramka na nim zabrała listę administracji i head
  // coachowi (TRE-1855).
  const canReadTeam = needsTeamRoster(permissions);
  const readsAssignedTeamOnly = rosterLimitedToAssigned(permissions);
  const canReadChat = usePermission(PermissionKey.CHAT_READ);
  const canReadSupport = usePermission(PermissionKey.SUPPORT_READ);
  const canManagePrograms = usePermission(PermissionKey.PROGRAMS_MANAGE);
  const canUseAgents = usePermission(PermissionKey.AGENTS_USE);
  const showTrainerAssistant = useTrainerAssistantEnabled();
  const trainerAssistantGutter = useTrainerAssistantGutter();
  const trainerAssistantWindowOpen = trainerAssistantGutter === TRAINER_ASSISTANT_WINDOW_GUTTER;
  const { enabled: isSalesCrmFeatureEnabled } = usePostHogFeatureFlag(SALES_CRM_FEATURE_FLAG);
  const { enabled: isChallengesFeatureEnabled } = usePostHogFeatureFlag(CHALLENGES_FEATURE_FLAG);
  const { enabled: isSupportBoardFeatureEnabled } = usePostHogFeatureFlag(SUPPORT_BOARD_FEATURE_FLAG);
  const { enabled: isAdsStudioFeatureEnabled } = usePostHogFeatureFlag(ADS_STUDIO_FEATURE_FLAG);
  // Unread assigned/mention/deadline badges for the support board menu item (TRE-1547).
  const { unreadCount: supportUnreadCount } = useSupportUnreadNotifications(
    isSupportBoardFeatureEnabled && canReadSupport,
  );
  const prevUnreadStatus = useRef<RequestStatus | null>(null);
  const prevChannelOpenCount = useRef(channelOpenCount);
  const prevIsPhone = useRef(isPhone);

  // TRE-2076: na telefonie nie ma pływających okienek czatu, więc każdy nowo otwarty
  // kanał pokazujemy na pełnym ekranie /chat. Kanał otwiera nie tylko drawer — także
  // „Wyślij wiadomość" w profilu klientki, tabela aktywności, alerty czatu i powiadomienia
  // (TRE-2095: warunek na otwartym drawerze zostawiał te przyciski bez żadnej reakcji).
  // Otwarcie poznajemy po liczniku, nie po długości kolejki — ponowne otwarcie kanału,
  // który już w niej jest, albo otwarcie przy pełnej kolejce nie zmienia długości.
  // Zwężenie okna poniżej progu telefonu z otwartym okienkiem też przenosi na /chat —
  // inaczej rozmowa (i niewysłany tekst w inpucie) znikałaby razem z okienkiem.
  useEffect(() => {
    const opened = channelOpenCount !== prevChannelOpenCount.current;
    const enteredPhone = isPhone && !prevIsPhone.current;
    prevChannelOpenCount.current = channelOpenCount;
    prevIsPhone.current = isPhone;
    if (!opened && !enteredPhone) {
      return;
    }
    // Tablet i zwężony desktop (768–1023 px): drawer (460 px) i pływające okienko obok siebie
    // nie mieszczą się w oknie, więc wybór kanału zamyka drawer i zostawia samo okienko.
    if (opened && showChatDrawer && isMobile) {
      toggleChatDrawer(false);
    }
    // TRE-2338: wszystkie okienka rozmów (do trzech) muszą zmieścić się obok okna asystenta, inaczej
    // asystent się zwija (zostaje bubble z rozmową), zamiast wypychać okienka na środek albo poza ekran.
    if (opened && !trainerAssistantWindowFits(activeChannelsCount)) {
      collapseTrainerAssistantWindow();
    }
    if (!isPhone || activeChannelsCount === 0) {
      return;
    }
    if (location.pathname !== "/chat") {
      navigate("/chat");
    }
  }, [channelOpenCount, activeChannelsCount, isPhone, isMobile, showChatDrawer, location.pathname, navigate]);

  // TRE-2338: lista kanałów czatu i okno asystenta stoją przy tej samej krawędzi, więc wygrywa to,
  // co otwarto później - inaczej okno asystenta leżało na liście, a okienka rozmów lądowały na środku.
  useEffect(() => {
    if (showChatDrawer) collapseTrainerAssistantWindow();
  }, [showChatDrawer]);
  useEffect(() => {
    if (trainerAssistantWindowOpen) toggleChatDrawer(false);
  }, [trainerAssistantWindowOpen]);

  useEffect(() => {
    if (
      canReadChat &&
      documentVisible &&
      unreadMessagesStatus === RequestStatus.SUBSCRIBED &&
      prevUnreadStatus.current === RequestStatus.SUBSCRIBED
    ) {
      // TRE-1646: refresh the data, leave the realtime channels alone. Re-subscribing on
      // every tab switch tore each channel down and rebuilt it, which piled up orphaned
      // subscriptions and produced join timeouts; realtime-js reconnects the socket by
      // itself, so only the data can be stale here.
      void dispatch(chatActions.fetchUnreadMessages());
      void dispatch(notificationsActions.refreshUnreadNotifications());

      if (canReadTeam) {
        void dispatch(teamActions.fetchTeamMembers(readsAssignedTeamOnly));
        if (isTeamMember) {
          void dispatch(teamActions.refreshTeamMember());
        }
      } else {
        void dispatch(teamActions.refreshTeamMember());
      }
    }

    prevUnreadStatus.current = unreadMessagesStatus;
  }, [canReadChat, documentVisible, unreadMessagesStatus, dispatch, canReadTeam, readsAssignedTeamOnly, isTeamMember]);

  useEffect(() => {
    if (canReadTeam) {
      dispatch(teamActions.subscribeToTeam(readsAssignedTeamOnly));
      if (isTeamMember) {
        void dispatch(teamActions.subscribeToTeamMember());
      }
    } else if (isTeamMember) {
      void dispatch(teamActions.subscribeToTeamMember());
    }
  }, [dispatch, canReadTeam, isTeamMember, readsAssignedTeamOnly]);

  useEffect(() => {
    if (!canReadChat) {
      return;
    }

    dispatch(chatActions.subscribeToUnreadMessages());

    return () => {
      dispatch(chatActions.unsubscribeFromUnreadMessages());
      dispatch(chatActions.unsubscribeFromLatestChannel());
    };
  }, [canReadChat, dispatch]);

  useEffect(
    () => () => {
      dispatch(teamActions.unsubscribeFromTeam());
      dispatch(teamActions.unsubscribeFromTeamMember());
    },
    [dispatch],
  );

  const getSelectedKey = () => {
    const activeNavItems = dashboardNavigation.filter((navItem) =>
      // matchPaths lets one menu entry stay selected across sibling sections (e.g. engagement
      // covers /challenges, /habits and /badges).
      (navItem.matchPaths ?? [navItem.path]).some((path) => location.pathname.startsWith(path)),
    );
    return activeNavItems?.[0] ? [activeNavItems[0].path] : undefined;
  };

  const batchActionItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "batch-add-workout",
        icon: <ThunderboltOutlined />,
        label: "Dodaj trening do wielu klientów",
        onClick: () => toggleBatchAddWorkoutModal(true),
      },
    ],
    [],
  );

  const navigationToRender = useMemo(
    () =>
      dashboardNavigation.filter((item) => {
        if (item.name === "support" && !isSupportBoardFeatureEnabled) {
          return false;
        }

        if (item.name === "engagement" && !isChallengesFeatureEnabled) {
          return false;
        }

        if (item.name === "sales" && !isSalesCrmFeatureEnabled) {
          return false;
        }

        if (item.name === "studio" && !isAdsStudioFeatureEnabled) {
          return false;
        }

        if (item.name === "dashboard") {
          return true;
        }

        // /team to taby (Członkowie/Rozliczenia/Statystyki) ograniczane osobno —
        // menu pokazuje Zespół, gdy przysługuje którykolwiek z nich.
        if (item.name === "team") {
          return matchesPermissionRequirement(permissions, dashboardPermissionRequirements.teamArea);
        }

        const permissionRequirement = getDashboardPermissionRequirement(item.name);
        return Boolean(permissionRequirement && matchesPermissionRequirement(permissions, permissionRequirement));
      }),
    [
      permissions,
      isSalesCrmFeatureEnabled,
      isSupportBoardFeatureEnabled,
      isChallengesFeatureEnabled,
      isAdsStudioFeatureEnabled,
    ],
  );

  return (
    <>
      <DashboardWrapper
        menu={
          <Menu theme="dark" mode="inline" selectedKeys={getSelectedKey()} style={{ backgroundColor: "transparent" }}>
            {navigationToRender.map((navItem) => {
              if (!navItem.inMenu) {
                return null;
              }
              return (
                <Menu.Item
                  key={navItem.path}
                  icon={navItem.icon}
                  onClick={() => navigate(navItem.path)}
                  className="bg-transparent font-medium text-white"
                  style={{ paddingLeft: 12, height: 48, lineHeight: 48 }}
                  disabled={navItem.disabled}
                >
                  {t(navItem.label)}
                  {navItem.path === "/support" && supportUnreadCount > 0 && (
                    <Badge count={supportUnreadCount} size="small" overflowCount={99} className="ml-2" />
                  )}
                </Menu.Item>
              );
            })}
          </Menu>
        }
        rightSection={
          <>
            {canManagePrograms || canUseAgents ? (
              <>
                {canManagePrograms ? (
                  <Dropdown menu={{ items: batchActionItems }} placement="bottomRight" trigger={["click", "hover"]}>
                    <div role="button" className="hidden md:block">
                      <PlusCircleOutlined style={{ fontSize: 26 }} className="rotate-icon" />
                    </div>
                  </Dropdown>
                ) : null}
                {canUseAgents ? <BackgroundTasksDrawer /> : null}
              </>
            ) : null}
            {isTeamMember && (
              <Tooltip title="Moje zadania">
                <div role="button" onClick={() => toggleTasksDrawer(true)}>
                  <Badge count={taskCounts.total} overflowCount={99}>
                    <CheckSquareOutlined style={{ fontSize: 26 }} className="rotate-icon" />
                  </Badge>
                </div>
              </Tooltip>
            )}
            {canReadChat ? (
              <div role="button" onClick={() => toggleChatDrawer(true)}>
                <Badge count={totalUnread} overflowCount={99}>
                  <MessageOutlined style={{ fontSize: 26 }} className="rotate-icon" />
                </Badge>
              </div>
            ) : null}
            <div role="button" onClick={() => toggleNotificationDrawer((prev) => !prev)}>
              <Badge overflowCount={99} count={unreadNotifications}>
                <BellOutlined style={{ fontSize: 26 }} className="rotate-icon" />
              </Badge>
            </div>

            <div role="button" onClick={() => toggleProfileDrawer((prev) => !prev)}>
              <Tooltip title={t("seeProfile")}>
                <Avatar
                  icon={!user?.profile ? <UserOutlined /> : null}
                  size="large"
                  shape="square"
                  src={user.profile?.avatarUrl || user.profile?.avatarUrl || null}
                >
                  {user?.profile?.firstName
                    ? getUserInitials(`${user.profile.firstName} ${user.profile.lastName ?? ""}`)
                    : null}
                </Avatar>
              </Tooltip>
            </div>
          </>
        }
      >
        <>
          <div className="h-full flex-1 bg-gray-100 p-4 lg:p-8">
            {user?.profile ? (
              <Outlet />
            ) : (
              <div className="flex min-h-[400px]	 flex-1 items-center justify-center">
                <Spin />
              </div>
            )}
          </div>

          <ProfileDrawer isOpen={showProfileDrawer} toggleDrawer={() => toggleProfileDrawer((prev) => !prev)} />
          {canReadChat ? (
            <ChatDrawer isOpen={showChatDrawer} toggleDrawer={() => toggleChatDrawer((prev) => !prev)} />
          ) : null}
          <NotificationsDrawer
            isOpen={showNotificationsDrawer}
            toggleDrawer={() => toggleNotificationDrawer((prev) => !prev)}
          />
          <TasksDrawer isOpen={showTasksDrawer} toggleDrawer={() => toggleTasksDrawer((prev) => !prev)} />
          {canManagePrograms ? (
            <BatchAddWorkoutModal open={showBatchAddWorkoutModal} onClose={() => toggleBatchAddWorkoutModal(false)} />
          ) : null}
          {canManagePrograms && canUseAgents ? (
            <TrainingProgramAgentHost bubbleOffset={showTrainerAssistant ? TRAINER_ASSISTANT_BUBBLE_GUTTER : 0} />
          ) : null}
          {showTrainerAssistant ? <TrainerAssistantHost /> : null}

          {canReadChat && !isPhone ? (
            <ChatUtilitiesProvider>
              <div
                className="fixed bottom-0 transition-transform"
                style={{
                  right: showTrainerAssistant ? trainerAssistantGutter : 0,
                  transform: `translateX(${showChatDrawer ? -CHAT_DRAWER_WIDTH : 0}px)`,
                  pointerEvents: "none",
                  zIndex: 1000,
                  transitionDuration: "0.4s",
                }}
              >
                <ChatWindowsList />
              </div>
            </ChatUtilitiesProvider>
          ) : null}
        </>
      </DashboardWrapper>
    </>
  );
};

export default Dashboard;
